
import axios from 'axios';

import _debounce from 'lodash/debounce';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  computed,
  watch,
} from 'vue';

import { useI18n } from 'vue-i18n';

import PrimeVueToast from 'primevue/toast';

import { useToast } from 'primevue/usetoast';

import { validate as validateUuid } from 'uuid';

import PrimeVueMessage from 'primevue/message';

import type {
  GenericObject,
  ErrorsMap,
  Countries,
  Region,
  Regions,
  Languages,
  Platform,
  Platforms,
  Browsers,
  Carriers,
  Project,
  Projects,
  Users,
  Endpoints,
  QualityPresets,
  Campaign,
  Creatives,
  CustomBid,
  AverageZoneBid,
} from '@/types';

import {
  TRAFFIC_TYPE_POP,
  TRAFFIC_TYPE_PUSH,
  TRAFFIC_TYPE_IN_PAGE_PUSH,
  TRAFFIC_TYPE_NATIVE,
  TRAFFIC_TYPE_BANNER,
  TRAFFIC_TYPES,
  TRAFFIC_TYPE_MIX,
  TRAFFIC_CATEGORY_MAINSTREAM,
  TRAFFIC_CATEGORIES,
  OPENRTB_DEVICE_TYPE_MOBILE_TABLET,
  OPENRTB_DEVICE_TYPE_PERSONAL_COMPUTER,
  REDIRECT_TYPE_META,
  REDIRECT_TYPES,
  TARGETING_TYPE_DISABLED,
  TARGETING_TYPE_WHITE_LIST,
  TARGETING_TYPES,
  TARGETING_TYPES_BY_TRAFFIC_SOURCE,
  TARGETING_TYPES_BY_TRAFFIC_CATEGORY,
  TARGETING_TYPES_BY_SUBSCRIBER_AGE,
  ENTRIES,
  USER_TYPE_AFFILIATE,
  CREATIVE_TYPE_PUSH,
  CREATIVE_TYPE_NATIVE,
  CREATIVE_TYPE_BANNER,
  CREATIVE_IMAGE_FORMAT_W_300_H_250,
  CREATIVE_IMAGE_FORMATS,
  CAMPAIGN_PAYMENT_TYPE_CPC,
  CAMPAIGN_PAYMENT_TYPE_CPA,
  CAMPAIGN_PAYMENT_TYPE_CPM,
  CAMPAIGN_PAYMENT_TYPES,
  CAMPAIGN_PAYMENT_TYPES_MAP,
  CAMPAIGN_AUTOBID_STRATEGY_TARGET_CPA,
  CAMPAIGN_AUTOBID_STRATEGY_TARGET_ROI,
  CAMPAIGN_AUTOBID_STRATEGIES,
  CONVERSION_EVENTS,
  CAMPAIGN_MAX_SPENDING_PER_DAY_STRATEGY_FAST,
  CAMPAIGN_MAX_SPENDING_PER_DAY_STRATEGIES,
  CAMPAIGN_CONNECTION_TYPE_ALL,
  CAMPAIGN_CONNECTION_TYPES,
  CAMPAIGN_APPROVAL_STATUS_PENDING_APPROVAL,
  CAMPAIGN_APPROVAL_STATUS_DECLINED,
  CAMPAIGN_APPROVAL_STATUSES,
  CAMPAIGN_STATUS_DISABLED,
  CAMPAIGN_STATUSES,
  CAMPAIGN_DEFAULT_REVSHARE,
  CAMPAIGN_DEFAULT_MAX_CLICKS_PER_IP,
} from '@/libs/consts';

import useProject from '@/composable/useProject';

import useUser from '@/composable/useUser';

import useFolderActions from '@/composable/useFolderActions';

import useEntries from '@/composable/useEntries';

import useEntriesNext from '@/composable/useEntries@next';

import ImageBase64Field from '@/components/ImageBase64Field.vue';

import CreativesComponent from '@/components/Creatives.vue';

export default defineComponent({
  components: {
    PrimeVueToast,
    PrimeVueMessage,
    ImageBase64Field,
    Macros: defineAsyncComponent(() => import('@/components/Macros.vue')),
    CustomBids: defineAsyncComponent(() => import('@/components/CustomBids.vue')),
    MaxClicksBySubids: defineAsyncComponent(() => import('@/components/MaxClicksBySubids.vue')),
    Creatives: CreativesComponent,
    TargetingByValues: defineAsyncComponent(() => import('@/components/TargetingByValues.vue')),
    FieldImageBase64Cropper: defineAsyncComponent(() => import('@/components/FieldImageBase64Cropper.vue')),
    ScheduleByHours: defineAsyncComponent(() => import('@/components/ScheduleByHours.vue')),
    ScheduleByDays: defineAsyncComponent(() => import('@/components/ScheduleByDays.vue')),
    ApprovalStatusComment: defineAsyncComponent(() => import('@/components/ApprovalStatusComment.vue')),
    AdSecure: defineAsyncComponent(() => import('@/views/AdSecure.vue')),
  },
  setup() {
    const folderActions = useFolderActions();

    const i18n = useI18n();

    const toast = useToast();

    const { activeProject: project }: { activeProject: Project } = useProject();

    const { activeUser: user } = useUser();

    const { getEntryByRoute } = useEntries();

    const trafficTypes = [...TRAFFIC_TYPES].filter((option) => [TRAFFIC_TYPE_POP, TRAFFIC_TYPE_PUSH, TRAFFIC_TYPE_IN_PAGE_PUSH, TRAFFIC_TYPE_NATIVE, TRAFFIC_TYPE_BANNER].includes(option.value as number));

    const {
      entries: countries,
      entriesLoading: countriesLoading,
      fetchEntries: fetchCountries,
    } = useEntriesNext<Countries>('/api/country/getCountries', ENTRIES);

    const {
      entries: regions,
      entriesLoading: regionsLoading,
      fetchEntries: fetchRegions,
    } = useEntriesNext<Regions>('/api/region/getRegions', ENTRIES);

    const {
      entries: languages,
      entriesLoading: languagesLoading,
      fetchEntries: fetchLanguages,
    } = useEntriesNext<Languages>('/api/language/getLanguages', ENTRIES);

    const {
      entries: platforms,
      entriesLoading: platformsLoading,
      fetchEntries: fetchPlatforms,
    } = useEntriesNext<Platforms>('/api/platform/getPlatforms', ENTRIES);

    const {
      entries: browsers,
      entriesLoading: browsersLoading,
      fetchEntries: fetchBrowsers,
    } = useEntriesNext<Browsers>('/api/browser/getBrowsers', ENTRIES);

    const {
      entries: carriers,
      entriesLoading: carriersLoading,
      fetchEntries: fetchCarriers,
    } = useEntriesNext<Carriers>('/api/carrier/getCarriers', ENTRIES);

    const {
      entries: projects,
      entriesLoading: projectsLoading,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects', ENTRIES);

    const {
      entries: users,
      entriesLoading: usersLoading,
      fetchEntries: fetchUsers,
    } = useEntriesNext<Users>('/api/user/getUsers', ENTRIES);

    const {
      entries: endpoints,
      entriesLoading: endpointsLoading,
      fetchEntriesViaGET: fetchEndpoints,
    } = useEntriesNext<Endpoints>('/api/v3/endpoints/options?include=project,user&paginate=false&with_unmanageable=true', ENTRIES);

    const {
      entries: qualityPresets,
      entriesLoading: qualityPresetsLoading,
      fetchEntries: fetchQualityPresets,
    } = useEntriesNext<QualityPresets>('/api/quality-preset/getQualityPresets', ENTRIES);

    const {
      entries: creatives,
      entriesLoading: creativesLoading,
      fetchEntries: fetchCreatives,
    } = useEntriesNext<Creatives>('/api/creative/getCreatives', ENTRIES);

    const campaign = reactive<Campaign>({
      traffic_type: TRAFFIC_TYPE_POP,
      mix_traffic_type: true,
      traffic_category: TRAFFIC_CATEGORY_MAINSTREAM,
      vertical_id: null,
      folder_ids: [],

      targeting_by_traffic_source: TARGETING_TYPE_DISABLED,
      targeting_by_traffic_category: TARGETING_TYPE_DISABLED,
      targeting_by_countries: TARGETING_TYPE_DISABLED,
      countries_ids: [],
      targeting_by_regions_ids: TARGETING_TYPE_DISABLED,
      regions_ids: [],
      targeting_by_cities_ids: TARGETING_TYPE_DISABLED,
      cities_ids: [],
      targeting_by_languages_codes: TARGETING_TYPE_DISABLED,
      languages_codes: [],
      targeting_by_platforms: TARGETING_TYPE_DISABLED,
      platforms_ids: [],
      targeting_by_browsers: TARGETING_TYPE_DISABLED,
      browsers_ids: [],
      targeting_by_subscriber_age: TARGETING_TYPE_DISABLED,
      connection_type: CAMPAIGN_CONNECTION_TYPE_ALL,
      targeting_by_ips: TARGETING_TYPE_DISABLED,
      ips: '',
      targeting_by_carriers_ids: TARGETING_TYPE_DISABLED,
      carriers_ids: [],
      targeting_by_endpoints_ids: TARGETING_TYPE_DISABLED,
      endpoints_ids: [],
      targeting_by_endpoints_uids: TARGETING_TYPE_DISABLED,
      endpoints_uids: [],
      targeting_by_referrers_hosts: TARGETING_TYPE_DISABLED,
      referrers_hosts: [],
      targeting_by_referrers_uuids: TARGETING_TYPE_DISABLED,
      referrers_uuids: [],
      targeting_by_subids: TARGETING_TYPE_DISABLED,
      subids_uuids: [],
      targeting_by_hours: TARGETING_TYPE_DISABLED,
      targeting_by_hours_timezone: 'Etc/GMT+0',
      hours: [],
      targeting_by_days: TARGETING_TYPE_DISABLED,
      targeting_by_days_timezone: 'Etc/GMT+0',
      days: [],
      use_quality_presets: false,
      quality_presets_ids: [],

      creatives: [],

      payment_type: CAMPAIGN_PAYMENT_TYPE_CPC,
      bid: 0,
      bids_by_countries_ids: [],
      bids_by_endpoints_uids: [],
      bids_by_referrers_uuids: [],
      bids_by_subids_uuids: [],
      ignore_max_bid: false,
      autobid_strategy: null,
      autobid_target_cpa: null,
      autobid_target_roi: null,
      autobid_min_cost: null,
      autobid_max_bid: null,
      autobid_conversion_event: null,
      bid_booster_chance: 0,
      bid_booster_multiplier: 1,
      revshare: CAMPAIGN_DEFAULT_REVSHARE,

      conversion_charge: false,
      conversion_charge_event: null,
      conversion_charge_amount: null,

      max_spending: null,
      spent_resets_monthly: false,
      max_spending_per_day: null,
      max_spending_per_endpoint_per_day: null,
      max_spending_per_day_strategy: CAMPAIGN_MAX_SPENDING_PER_DAY_STRATEGY_FAST,

      test_max_spending: null,
      test_max_spending_per_endpoint: null,
      test_max_spending_per_subid: null,

      max_clicks_per_ip: CAMPAIGN_DEFAULT_MAX_CLICKS_PER_IP,
      max_clicks_per_subid: 0,
      max_clicks_by_subids_uuids: [],

      start_stop_at_timezone: 'Etc/GMT+0',
      start_at: null,
      stop_at: null,
      filter_ip_mismatch: false,
      filter_user_agent_mismatch: false,
      filter_referrer_mismatch: false,
      filter_proxy: true,
      filter_has_no_js: false,
      filter_iframe: false,
      filter_headless: false,
      filter_double_click: false,
      filter_click_without_impression: false,

      is_builtin: false,
      redirect_type: REDIRECT_TYPE_META,

      adsecure: true,
      has_violations: false,

      creative_image_format: null,

      approval_status: CAMPAIGN_APPROVAL_STATUS_PENDING_APPROVAL,
      approval_status_comment: null,
      status: CAMPAIGN_STATUS_DISABLED,
    });

    const campaignErrorsMap = ref<ErrorsMap>({});

    const paymentTypeOptions = computed(() => CAMPAIGN_PAYMENT_TYPES.map((option) => (
      option.value === CAMPAIGN_PAYMENT_TYPE_CPM && campaign.traffic_type === TRAFFIC_TYPE_POP
        ? { ...option, disabled: true }
        : option
    )));

    const trafficTypeOptions = computed(() => trafficTypes.map((option) => (
      option.value === TRAFFIC_TYPE_POP && campaign.payment_type === CAMPAIGN_PAYMENT_TYPE_CPM
        ? { ...option, disabled: true }
        : option
    )));

    const isPush = computed((): boolean => [TRAFFIC_TYPE_PUSH, TRAFFIC_TYPE_IN_PAGE_PUSH].includes(campaign.traffic_type as number));

    const isNative = computed((): boolean => campaign.traffic_type === TRAFFIC_TYPE_NATIVE);

    const isBanner = computed((): boolean => campaign.traffic_type === TRAFFIC_TYPE_BANNER);

    const canHasCreatives = computed(() => isPush.value || isNative.value || isBanner.value);

    const canUseAutobid = computed((): boolean => user.isStaff() || Boolean(user.show_autobid));

    const creativeTypeFilter = computed((): number|null => {
      if (isPush.value) {
        return CREATIVE_TYPE_PUSH;
      }

      if (isNative.value) {
        return CREATIVE_TYPE_NATIVE;
      }

      if (isBanner.value) {
        return CREATIVE_TYPE_BANNER;
      }

      return null;
    });

    const macros = computed((): string[] => {
      // Общие макросы для всех типов трафика
      const macrosCommon = [
        'click_uuid',
        'bid_cpc',
        'bid_cpm',
        'zone_uid',
        'subid_uuid',
        'referer_uuid',
        'campaign_id',
        'country_alpha2_code',
        'city',
        'carrier',
        'platform_name',
        'browser_name',
        'creative_id',
      ];

      // Макросы для Push и In-Page кампаний
      if (isPush.value) {
        macrosCommon.push(...[
          'subscriber_uuid',
          'subscriber_age_date',
          'subscriber_age_timestamp',
          'subscriber_age_days',
          'creative_id',
        ]);
      }

      return macrosCommon;
    });

    const fetchRegionsWrapper = async (): Promise<void> => fetchRegions({
      country_id: campaign.countries_ids,
    });

    // Справочник городов живёт на сервере: выбранные регионы работают фильтром выдачи.
    // sort=name — единственная разрешённая сортировка у CityService
    const citiesParams = computed(() => ({
      'filter[region_id]': campaign.regions_ids?.join(','),
      sort: 'name',
    }));

    const fetchUsersWrapper = async (): Promise<void> => fetchUsers({
      project_id: user.isSuperAdmin() ? campaign.project_id : null,
      type: USER_TYPE_AFFILIATE,
    });

    // Правило адсервера (campaign.go, «Тип трафика»): без mix_traffic_type кампания идёт
    // только в свой тип, с ним — во весь набор. Несовместимые эндпоинты в списке ничего
    // не значат: адсервер их всё равно отбросит
    const endpointsTrafficTypes = computed((): number[] => (campaign.mix_traffic_type
      ? TRAFFIC_TYPE_MIX.get(campaign.traffic_type)
      : [campaign.traffic_type as number]));

    const fetchEndpointsWrapper = async (): Promise<void> => fetchEndpoints({
      'filter[project_id]': user.isSuperAdmin() ? campaign.project_id : null,
      'filter[traffic_type]': endpointsTrafficTypes.value.join(','),
    });

    const fetchQualityPresetsWrapper = async (): Promise<void> => fetchQualityPresets({
      project_id: user.isSuperAdmin() ? campaign.project_id : null,
      public: true,
    });

    const fetchCreativesWrapper = async (): Promise<void> => fetchCreatives({
      project_id: user.isSuperAdmin() ? campaign.project_id : null,
      user_id: user.isStaff() ? campaign.user_id : null,
      type: creativeTypeFilter.value,
      image_format: campaign.creative_image_format,
    });

    const fetchCampaign = async (): Promise<void> => {
      const entry = await getEntryByRoute('/api/campaign/getCampaignById');

      if (entry) {
        Object.assign(campaign, entry);
      }

      Promise.all([
        fetchCountries(),
        campaign.countries_ids?.length ? fetchRegionsWrapper() : Promise.resolve(),
        fetchLanguages(),
        fetchPlatforms(),
        fetchBrowsers(),
        fetchCarriers(),
        user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
        user.isStaff() ? fetchUsersWrapper() : Promise.resolve(),
        user.isStaff() ? fetchEndpointsWrapper() : Promise.resolve(),
        fetchQualityPresetsWrapper(),
        fetchCreativesWrapper(),
      ]);
    };

    const storeCampaign = async (): Promise<void> => {
      // Папки — не поле кампании, а отдельная связь: у каждого пользователя своя, поэтому
      // сохраняются вторым запросом, уже по id. Выбор снимается заранее: ответ вернёт папки,
      // сохранённые в базе, и затрёт его.
      const folderIds = [...(campaign.folder_ids || [])];

      const response = await axios.post('/api/campaign/storeCampaign', campaign);

      Object.assign(campaign, response.data);

      // Ответ вернул папки, сохранённые в базе. Если выбор им и так равен, второй запрос не нужен.
      const storedFolderIds: number[] = response.data.folder_ids || [];

      const foldersChanged = folderIds.length !== storedFolderIds.length
        || [...folderIds].sort().join() !== [...storedFolderIds].sort().join();

      if (!foldersChanged || !campaign.id) {
        return;
      }

      try {
        await folderActions.setCampaignFolders(campaign.id, folderIds);

        campaign.folder_ids = folderIds;
      } catch (error) {
        // Кампания к этому моменту уже сохранена, поэтому про сбой папок нужно сказать
        // отдельно — общий текст ошибки прочитался бы как «кампания не сохранилась».
        toast.add({
          severity: 'error',
          summary: i18n.t('error'),
          detail: i18n.t('folders_store_error'),
          life: 4000,
        });

        throw error;
      }
    };

    const cloneCampaign = (): void => {
      campaign.id = undefined;

      campaign.name += ` - ${i18n.t('copy_female')}`;
    };

    const onUserChange = (): void => {
      campaign.creatives = [];

      fetchCreativesWrapper();
    };

    const onProjectChange = (): void => {
      campaign.user_id = undefined;

      fetchUsersWrapper();

      campaign.quality_presets_ids = [];

      fetchEndpointsWrapper();

      fetchQualityPresetsWrapper();

      onUserChange();
    };

    const onTrafficTypeChange = (): void => {
      campaign.creative_image_format = null;

      if (canHasCreatives.value) {
        campaign.creatives = [];

        fetchCreativesWrapper();
      }

      // Для Push кампаний такие фильтры не нужны
      if (campaign.traffic_type === TRAFFIC_TYPE_PUSH) {
        campaign.filter_ip_mismatch = false;

        campaign.filter_user_agent_mismatch = false;
      }

      // Для Native пока ограничиваемся форматом 300x250
      if (campaign.traffic_type === TRAFFIC_TYPE_NATIVE) {
        campaign.creative_image_format = CREATIVE_IMAGE_FORMAT_W_300_H_250;
      }
    };

    const onCreativeImageFormatChange = (): void => {
      campaign.creatives = [];

      fetchCreativesWrapper();
    };

    // Города фильтруются выбранными регионами, поэтому опустевший список регионов
    // обнуляет и их. Города оставшихся регионов отсеивает сам FieldListBoxRemote:
    // проверить принадлежность можно только запросом, выдача постраничная
    const onRegionsIdsChange = (): void => {
      if (campaign.regions_ids?.length) {
        return;
      }

      campaign.targeting_by_cities_ids = TARGETING_TYPE_DISABLED;

      campaign.cities_ids = [];
    };

    const onCountriesIdsChange = async (): Promise<void> => {
      if (!campaign.countries_ids?.length) {
        campaign.targeting_by_regions_ids = TARGETING_TYPE_DISABLED;

        campaign.regions_ids = [];

        onRegionsIdsChange();

        return;
      }

      await fetchRegionsWrapper();

      // Убираем регионы стран, которые больше не выбраны в списке

      campaign.regions_ids = campaign.regions_ids?.filter((regionId) => regions.data.map((region: Region) => region.id).includes(regionId));

      onRegionsIdsChange();
    };

    const selectDesktopPlatforms = (): void => {
      campaign.platforms_ids = platforms.data
        .filter((platform: Platform) => platform.device_type === OPENRTB_DEVICE_TYPE_PERSONAL_COMPUTER)
        .map((platform: Platform) => platform.id);
    };

    const selectMobilePlatforms = (): void => {
      campaign.platforms_ids = platforms.data
        .filter((platform: Platform) => platform.device_type === OPENRTB_DEVICE_TYPE_MOBILE_TABLET)
        .map((platform: Platform) => platform.id);
    };

    // Средний бид спроса на зонах CJ@ и MS@
    const averageZoneBids = ref<AverageZoneBid[]>([]);

    const isEndpointsTargeted = computed((): boolean => campaign.targeting_by_endpoints_ids === TARGETING_TYPE_WHITE_LIST
      && !!campaign.endpoints_ids?.length);

    const isZonesTargeted = computed((): boolean => campaign.targeting_by_endpoints_uids === TARGETING_TYPE_WHITE_LIST
      && !!campaign.endpoints_uids?.length);

    // Только там, где кнопке есть где появиться
    const isAverageZoneBidRequestable = computed((): boolean => user.isStaff()
      && !campaign.uses_bulk_segment_bids
      && campaign.payment_type !== CAMPAIGN_PAYMENT_TYPE_CPA
      && !!campaign.bids_by_endpoints_uids?.length
      && (isEndpointsTargeted.value || isZonesTargeted.value));

    // Форма может быть не сохранена: отправляем то, что сотрудник видит перед собой
    const averageZoneBidRequestBody = computed((): GenericObject => ({
      pricing_model: campaign.payment_type,
      targeting_by_countries: campaign.targeting_by_countries,
      country_ids: campaign.countries_ids || [],
    }));

    const fetchAverageZoneBids = async (): Promise<void> => {
      if (!isAverageZoneBidRequestable.value) {
        averageZoneBids.value = [];

        return;
      }

      try {
        const response = await axios.post('/api/v3/tools/average-zone-bids', averageZoneBidRequestBody.value);

        averageZoneBids.value = response.data.data;
      } catch (error) {
        // Подсказка, а не часть формы: об ошибке не сообщаем
        averageZoneBids.value = [];
      }
    };

    // Кэша у расчёта нет, а страны правят чекбоксами
    watch(
      [isAverageZoneBidRequestable, averageZoneBidRequestBody],
      _debounce(fetchAverageZoneBids, 500),
      { immediate: true },
    );

    // Строки списка ставок, попавшие в зоны группы
    const buildAverageZoneBidMatches = (
      customBids: CustomBid[],
    ): { averageBid: AverageZoneBid; bids: CustomBid[] }[] => {
      const endpointsIds = new Set(isEndpointsTargeted.value ? campaign.endpoints_ids : []);

      const zonesUids = new Set((isZonesTargeted.value ? campaign.endpoints_uids || [] : []).map(Number));

      return averageZoneBids.value
        .map((averageBid: AverageZoneBid) => {
          const uids = new Set(averageBid.endpoints
            .filter((endpoint) => endpointsIds.has(endpoint.id) || zonesUids.has(endpoint.uid))
            .map((endpoint) => endpoint.uid));

          return {
            averageBid,
            bids: customBids.filter((customBid: CustomBid) => uids.has(Number(customBid.key))),
          };
        })
        .filter(({ bids }) => bids.length);
    };

    const applyAverageZoneBids = (customBids: CustomBid[], refresh: () => void): void => {
      const matches = buildAverageZoneBidMatches(customBids);

      // API v3 номинирует ставку за тысячу единиц, а v1 хранит её за одну
      const applied = matches
        .flatMap(({ averageBid, bids }) => bids.map((customBid: CustomBid) => Object.assign(customBid, { bid: averageBid.bid / 1000 })));

      refresh();

      toast.add({
        severity: 'success',
        summary: i18n.t('average_bid_applied'),
        detail: i18n.t('zones_updated', { count: applied.length }),
        life: 4000,
      });
    };

    const previewCreative = (creativeData: GenericObject): void => {
      toast.removeAllGroups();

      const creative = creatives.data.find((entry) => entry.id === creativeData.creative_id);

      toast.add({
        severity: 'info',
        styleClass: 'p-toast-push',
        detail: creative,
        group: 'bc',
        life: 5000,
      });
    };

    return {
      TRAFFIC_TYPE_POP,
      TRAFFIC_TYPE_PUSH,
      TRAFFIC_TYPE_NATIVE,
      TRAFFIC_CATEGORIES,
      REDIRECT_TYPES,
      TARGETING_TYPE_DISABLED,
      TARGETING_TYPES,
      TARGETING_TYPES_BY_TRAFFIC_SOURCE,
      TARGETING_TYPES_BY_TRAFFIC_CATEGORY,
      TARGETING_TYPES_BY_SUBSCRIBER_AGE,
      CAMPAIGN_PAYMENT_TYPE_CPC,
      CAMPAIGN_PAYMENT_TYPE_CPA,
      CAMPAIGN_PAYMENT_TYPE_CPM,
      CAMPAIGN_PAYMENT_TYPES_MAP,
      paymentTypeOptions,
      buildAverageZoneBidMatches,
      applyAverageZoneBids,
      CAMPAIGN_AUTOBID_STRATEGY_TARGET_CPA,
      CAMPAIGN_AUTOBID_STRATEGY_TARGET_ROI,
      CAMPAIGN_AUTOBID_STRATEGIES,
      CONVERSION_EVENTS,
      CAMPAIGN_MAX_SPENDING_PER_DAY_STRATEGY_FAST,
      CAMPAIGN_MAX_SPENDING_PER_DAY_STRATEGIES,
      CAMPAIGN_CONNECTION_TYPES,
      CAMPAIGN_APPROVAL_STATUS_DECLINED,
      CAMPAIGN_APPROVAL_STATUSES,
      CAMPAIGN_STATUSES,
      CREATIVE_IMAGE_FORMATS,
      project,
      user,
      trafficTypeOptions,
      countriesLoading,
      countries,
      regionsLoading,
      regions,
      languagesLoading,
      languages,
      platformsLoading,
      platforms,
      browsersLoading,
      browsers,
      carriersLoading,
      carriers,
      projectsLoading,
      projects,
      usersLoading,
      users,
      endpointsLoading,
      endpoints,
      qualityPresetsLoading,
      qualityPresets,
      creativesLoading,
      creatives,
      campaign,
      campaignErrorsMap,
      isBanner,
      canHasCreatives,
      canUseAutobid,
      macros,
      validateUuid,
      fetchCampaign,
      storeCampaign,
      cloneCampaign,
      previewCreative,
      onProjectChange,
      onUserChange,
      onTrafficTypeChange,
      onCreativeImageFormatChange,
      citiesParams,
      onCountriesIdsChange,
      onRegionsIdsChange,
      selectDesktopPlatforms,
      selectMobilePlatforms,
    };
  },
});
